import moment from 'moment';
import config from 'src/shared/config';
import axios from 'src/shared/utils/axios';
import { toLocalDateTimeStr } from 'src/shared/utils/localDateTime';
import { StringLiteralLike } from 'typescript';
import DummyApi from '../dummyApi';
import { ApprovalDetailDto, ApprovalDto, ApprovalType, ApproverDto } from './approvalApi';
const FXM_API_ENABLED = config.servers.fxm.enabled;
const FXM_API_URL = config.servers.fxm.url;
export enum PrintTargetType {
  WATERMARK_PRINT = 'WATERMARK_PRINT',
  DETECTION_PRINT = 'DETECTION_PRINT',
}
export enum PrintTargetDocType {
  NONE = 'NONE', // only UI, is null in API
  ALL = 'ALL',
  NORMAL_DOC = 'NORMAL_DOC',
  SECURE_DOC = 'SECURE_DOC',
}
export enum PrintDocType {
  NONE = 'NONE', // only UI, is null in API
  PRINT = 'PRINT',
  WATPRINT = 'WATPRINT',
  USERSELECT = 'USERSELECT',
}
export interface PrintPeriodApprovalReqDto extends ApprovalDto {
  startDate: number; // 요청 시작 시간
  endDate: number; // 요청 종료 시간
  watermarkPrint: boolean; // 적용 범위 > 워터마크 예외(Y/N)
  detectionPrint: boolean; // 적용 범위 > 민감 정보 (Y/N)
  targetDoc: PrintTargetDocType;
  norDocPrint: PrintDocType; // 일반 문서 인쇄 방식
  secDocPrint: PrintDocType; // 보안 문서 인쇄 방식
}
export interface PrintPeriodApprovalReqEntity extends PrintPeriodApprovalReqDto {
  startDateStr: string;
  endDateStr: string;
}
function toEntity(dto: PrintPeriodApprovalReqDto): PrintPeriodApprovalReqEntity {
  return {
    ...dto,
    startDateStr: toLocalDateTimeStr(dto.startDate),
    endDateStr: toLocalDateTimeStr(dto.endDate),
  };
}
export const defaultPrintPeriodApprovalReqEntity: PrintPeriodApprovalReqEntity = {
  code: '',
  approvalStep: 1, // 신청 단계(정책에 따라 1~N까지 나뉨, 자가 승인일 경우 1)
  currentStep: 1, //신청 시 단계, 1 고정
  approvalType: ApprovalType.PRINT_PERIOD,
  status: '',
  approvalMethod: '',
  remark: '',
  approvers: [],
  startDate: moment().startOf('d').valueOf(),
  endDate: moment().endOf('d').valueOf(),
  watermarkPrint: false, // 적용 범위 > 워터마크 예외(Y/N)
  detectionPrint: false, // 적용 범위 > 민감 정보 (Y/N)
  targetDoc: PrintTargetDocType.NONE,
  norDocPrint: PrintDocType.NONE, // 일반 문서 인쇄 방식
  secDocPrint: PrintDocType.NONE, // 보안 문서 인쇄 방식
  startDateStr: '',
  endDateStr: '',
};
export interface PrintPeriodApprovalServerReqDto extends ApprovalDto {
  startDate: number; // 요청 시작 시간
  endDate: number; // 요청 종료 시간
  watermarkPrint: string; // 적용 범위 > 워터마크 예외(Y/N)
  detectionPrint: string; // 적용 범위 > 민감 정보 (Y/N)
  targetDoc: string | null;
  norDocPrint: string | null; // 일반 문서 인쇄 방식
  secDocPrint: string | null; // 보안 문서 인쇄 방식
}
const convertPrintPeriodReqDtoToServerDto = (
  reqDto: PrintPeriodApprovalReqDto
): PrintPeriodApprovalServerReqDto => {
  return {
    code: reqDto.code,
    approvalStep: reqDto.approvalStep,
    currentStep: reqDto.currentStep,
    approvalType: reqDto.approvalType,
    status: reqDto.status,
    approvalMethod: reqDto.approvalMethod,
    remark: reqDto.remark,
    approvers: reqDto.approvers,
    startDate: reqDto.startDate,
    endDate: reqDto.endDate,
    watermarkPrint: reqDto.watermarkPrint ? 'Y' : 'N',
    detectionPrint: reqDto.detectionPrint ? 'Y' : 'N',
    targetDoc: reqDto.targetDoc === PrintTargetDocType.NONE ? null : reqDto.targetDoc.valueOf(),
    norDocPrint: reqDto.norDocPrint === PrintDocType.NONE ? null : reqDto.norDocPrint.valueOf(),
    secDocPrint: reqDto.secDocPrint === PrintDocType.NONE ? null : reqDto.secDocPrint.valueOf(),
  };
};
export interface PrintPeriodApprovalDetailDto extends ApprovalDetailDto {
  startDate: number;
  endDate: number;
  printPolStatus: string; // 기간 내 예외 상태
  watermarkPrint: string; // 적용 범위 > 워터마크 예외(Y/N)
  detectionPrint: string; // 적용 범위 > 민감 정보 (Y/N)
  targetDoc: string | null;
  norDocPrint: string | null; // 일반 문서 인쇄 방식
  secDocPrint: string | null; // 보안 문서 인쇄 방식
  // applyType: string;
}
export interface PrintPeriodApprovalDetailEntity extends PrintPeriodApprovalDetailDto {
  startDateStr: string;
  endDateStr: string;
  applyDateStr: string;
  updatedTsStr: string;
  useWatermarkPrint: boolean; // 적용 범위 > 워터마크 예외(Y/N)
  useDetectionPrint: boolean; // 적용 범위 > 민감 정보 (Y/N)
  targetDocType: PrintTargetDocType;
  norDocPrintType: PrintDocType; // 일반 문서 인쇄 방식
  secDocPrintType: PrintDocType; // 보안 문서 인쇄 방식
}
const convertPrintPeriodDetailDtoToEntity = (
  dto: PrintPeriodApprovalDetailDto
): PrintPeriodApprovalDetailEntity => {
  return {
    ...dto,
    startDateStr: dto.startDate > 0 ? toLocalDateTimeStr(dto.startDate) : '',
    endDateStr: dto.endDate > 0 ? toLocalDateTimeStr(dto.endDate) : '',
    useWatermarkPrint: dto.watermarkPrint.toUpperCase() === 'Y' ? true : false,
    useDetectionPrint: dto.detectionPrint.toUpperCase() === 'Y' ? true : false,
    targetDocType:
      dto.targetDoc === null ? PrintTargetDocType.NONE : PrintTargetDocType[dto.targetDoc],
    norDocPrintType: dto.norDocPrint === null ? PrintDocType.NONE : PrintDocType[dto.norDocPrint],
    secDocPrintType: dto.secDocPrint === null ? PrintDocType.NONE : PrintDocType[dto.secDocPrint],
    applyDateStr: dto.applyDate > 0 ? toLocalDateTimeStr(dto.applyDate) : '',
    updatedTsStr: dto.updatedTs > 0 ? toLocalDateTimeStr(dto.updatedTs) : '',
  };
};
interface PrintPeriodApprovalApi {
  find(code: string): Promise<PrintPeriodApprovalDetailEntity>;
  apply(reqDto: PrintPeriodApprovalReqDto): Promise<PrintPeriodApprovalDetailEntity>;
  cancel(approvalNo: string): Promise<boolean>;
  carryout(approvalNo: string): Promise<boolean>;
}
class PrintPeriodApprovalServerApi implements PrintPeriodApprovalApi {
  async find(code: string): Promise<PrintPeriodApprovalDetailEntity> {
    const resp = await axios.get<PrintPeriodApprovalDetailDto>(
      `${FXM_API_URL}/api/printpolicy/find/${code}`
    );
    return convertPrintPeriodDetailDtoToEntity(resp.data);
  }
  async apply(reqDto: PrintPeriodApprovalReqDto): Promise<PrintPeriodApprovalDetailEntity> {
    const reqServerDto: PrintPeriodApprovalServerReqDto =
      convertPrintPeriodReqDtoToServerDto(reqDto);
    const resp = await axios.post<PrintPeriodApprovalDetailDto>(
      `${FXM_API_URL}/api/printpolicy/apply`,
      reqServerDto
    );
    return convertPrintPeriodDetailDtoToEntity(resp.data);
  }
  async cancel(approvalNo: string): Promise<boolean> {
    const resp = await axios.get<PrintPeriodApprovalDetailDto>(
      `${FXM_API_URL}/api/printpolicy/cancel/${approvalNo}`
    );
    if ([400, 500].includes(resp.status)) {
      return false;
    }
    return true;
  }
  async carryout(approvalNo: string): Promise<boolean> {
    const resp = await axios.get<PrintPeriodApprovalDetailDto>(
      `${FXM_API_URL}/api/printpolicy/carryout/${approvalNo}`
    );
    if ([400, 500].includes(resp.status)) {
      return false;
    }
    return true;
  }
}
const printPeriodApprovalApi = new PrintPeriodApprovalServerApi();
export default printPeriodApprovalApi;
